import React, { useCallback } from 'react'
import { Handle, NodeProps, Position } from '@xyflow/react'
import { useUIStore } from '@/stores/uiStore'
import { useLayoutStore } from '@/stores/layoutStore'
import { Zone, DeviceCategory } from '@/types/devices'
import { CATEGORY_COLORS, CATEGORY_LABELS } from '@/constants/categories'
import { SIGNAL_TYPE_COLORS } from '@/constants/colors'

type MemberDevice = {
  id: string
  name: string
  category: DeviceCategory
}

interface ZoneNodeData extends Zone {
  deviceCount?: number
  memberDevices?: MemberDevice[]
  onEdit?: () => void
}

const MAX_LISTED_DEVICES = 20

const edgeToPosition: Record<string, Position> = {
  top: Position.Top,
  right: Position.Right,
  bottom: Position.Bottom,
  left: Position.Left,
}

/**
 * ZoneNode
 *
 * Overview mode (data.collapsed): renders the zone as a compact card
 * listing up to MAX_LISTED_DEVICES devices inside it, each with a
 * category-color dot. React Flow Handles are drawn for every
 * `ZoneConnectionPoint` on the zone so inter-zone edges can attach
 * to the user-chosen boundary ports.
 *
 * Full mode: renders the classic dashed-outline zone container used
 * as a visual background for devices positioned inside it.
 */
const ZoneNode = React.memo((props: NodeProps<any>) => {
  const data = props.data as ZoneNodeData
  const focusedZoneId = useUIStore(s => s.focusedZoneId)
  const setFocusedZoneId = useUIStore(s => s.setFocusedZoneId)
  const deleteZone = useLayoutStore(s => s.deleteZone)

  const isFocused = focusedZoneId === data.id
  const borderColor = data.color
  const backgroundColor = data.color + '10'
  const focusedBorderColor = isFocused ? data.color : data.color + '80'

  const handleDoubleClick = useCallback(() => {
    setFocusedZoneId(isFocused ? null : data.id)
  }, [isFocused, data.id, setFocusedZoneId])

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      deleteZone(data.id)
    },
    [data.id, deleteZone],
  )

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      data.onEdit?.()
    },
    [data],
  )

  const connectionPoints = data.connectionPoints ?? []

  const boundaryHandles = connectionPoints.map(cp => (
    <Handle
      key={cp.id}
      id={cp.id}
      // React Flow wires up connections source→target. Map the
      // zone's view of the port direction accordingly: an exposed
      // input port is a connection `target` (signal comes in), an
      // exposed output port is a connection `source` (signal goes
      // out). Without this, two zones both expose 'source' handles
      // and the edge never snaps.
      type={cp.direction === 'input' ? 'target' : 'source'}
      position={edgeToPosition[cp.edge] ?? Position.Right}
      style={{
        background: SIGNAL_TYPE_COLORS[cp.signalType] ?? '#999',
        width: 10,
        height: 10,
        border: '2px solid #0f172a',
        // Offset the handle along its edge by the configured position
        // (0..1). React Flow positions handles relative to the node's
        // bounding box, so we convert to a percentage on the matching
        // axis.
        ...(cp.edge === 'top' || cp.edge === 'bottom'
          ? { left: `${cp.position * 100}%` }
          : { top: `${cp.position * 100}%` }),
      }}
      title={`${cp.name} (${cp.signalType})`}
    />
  ))

  if (data.collapsed) {
    const listed = data.memberDevices ?? []
    const totalCount = data.deviceCount ?? listed.length
    const hiddenCount = Math.max(0, totalCount - listed.length)

    return (
      <div
        onDoubleClick={handleDoubleClick}
        className="relative select-none"
        style={{ minWidth: '220px' }}
      >
        <div
          className="rounded-xl border-2 border-dashed relative group hover:shadow-lg transition-shadow"
          style={{
            borderColor: focusedBorderColor,
            backgroundColor: isFocused ? data.color + '18' : data.color + '08',
          }}
        >
          {/* Header: color dot + zone name + device count pill */}
          <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-2">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: borderColor }}
              />
              <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {data.name}
              </span>
            </div>
            <span
              className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ backgroundColor: data.color + '30', color: data.color }}
            >
              {totalCount} {totalCount === 1 ? 'device' : 'devices'}
            </span>
          </div>

          {/* Member device list */}
          {listed.length > 0 && (
            <ul className="px-3 pb-3 space-y-1 max-h-[360px] overflow-y-auto">
              {listed.map(device => (
                <li
                  key={device.id}
                  className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200"
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: CATEGORY_COLORS[device.category],
                    }}
                    title={CATEGORY_LABELS[device.category]}
                  />
                  <span className="truncate">{device.name}</span>
                </li>
              ))}
              {hiddenCount > 0 && (
                <li className="text-[10px] text-gray-500 dark:text-gray-400 italic pl-4">
                  +{hiddenCount} more
                </li>
              )}
            </ul>
          )}

          {/* Hover actions: edit + delete */}
          <div className="absolute -top-2 -right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={handleEdit}
              className="w-5 h-5 rounded-full bg-accent hover:bg-[color:var(--accent-hover)] text-[color:var(--accent-ink)] flex items-center justify-center shadow-md text-[10px]"
              title="Edit zone (expose ports, rename, etc.)"
            >
              ✏
            </button>
            <button
              onClick={handleDelete}
              className="w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md"
              title="Delete zone"
            >
              <span className="text-xs leading-none">×</span>
            </button>
          </div>

          {/* Empty-state help: no ports exposed yet */}
          {connectionPoints.length === 0 && (
            <div
              className="px-3 pb-2 -mt-1 text-[10px] text-gray-500 dark:text-gray-400 italic"
              title="Open the zone editor to expose internal device ports on the zone boundary"
            >
              No boundary ports — click ✏ to expose some
            </div>
          )}

          {boundaryHandles}
        </div>
      </div>
    )
  }

  return (
    <div
      onDoubleClick={handleDoubleClick}
      className="relative select-none transition-all group"
    >
      <div
        className="h-full rounded-xl border-2 border-dashed relative flex flex-col"
        style={{
          borderColor: focusedBorderColor,
          backgroundColor,
          boxShadow: isFocused ? `0 0 12px ${data.color}40` : 'none',
        }}
      >
        {/* Zone name label - positioned top-left outside */}
        <div className="absolute -top-6 left-0 flex items-center gap-1.5">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: borderColor }}
          />
          <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
            {data.name}
          </span>
          {data.description && (
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
              {data.description}
            </span>
          )}
        </div>

        {/* Device count badge */}
        <div className="absolute -top-6 right-0 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white/50 dark:bg-gray-700/50 px-2 py-0.5 rounded">
          {data.deviceCount || 0} device
          {(data.deviceCount || 0) !== 1 ? 's' : ''}
        </div>

        {/* Delete button on hover */}
        <button
          onClick={handleDelete}
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10"
          title="Delete zone"
        >
          <span className="text-xs leading-none">×</span>
        </button>

        {boundaryHandles}
      </div>
    </div>
  )
}) as any

export default ZoneNode
// MAX_LISTED_DEVICES is the cap from EditorClient's memberDevices slice,
// exposed here so it's visible alongside the rendering code.
export { MAX_LISTED_DEVICES }
